import { useEffect, useRef, useState } from "react";
import { api, API } from "../api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Upload, Download, Search, Pencil, Trash2, Merge } from "lucide-react";

const emptyForm = { name: "", phone: "", whatsapp: "", email: "", address: "", tag: "regular", notes: "" };
const rupee = (v) => "₹ " + Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const tagStyles = {
  regular: "bg-neutral-100 text-neutral-800 border-neutral-200",
  vip: "bg-amber-100 text-amber-800 border-amber-200",
  "one-time": "bg-neutral-100 text-neutral-600 border-neutral-200",
};

export default function Customers() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const fileRef = useRef(null);

  const load = async () => {
    const params = {};
    if (search) params.search = search;
    if (tagFilter !== "all") params.tag = tagFilter;
    const { data } = await api.get("/customers", { params });
    setItems(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, tagFilter]);

  const submit = async () => {
    try {
      if (editing) {
        await api.put(`/customers/${editing.id}`, form);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", form);
        toast.success("Customer added");
      }
      setOpen(false); setEditing(null); setForm(emptyForm); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this customer?")) return;
    try { await api.delete(`/customers/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed (admin only)"); }
  };

  const importCsv = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/customers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const merged = data.merged || 0;
      toast.success(`Imported ${data.added} new customer${data.added===1?"":"s"}${merged ? `, updated ${merged} existing (matched by phone number)` : ""}`); load();
    } catch (err) { toast.error("Import failed"); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const mergeDuplicates = async () => {
    if (!window.confirm("Merge any customers that share the same phone number into one record? This can't be undone.")) return;
    try {
      const { data } = await api.post("/customers/merge-duplicates");
      if (!data.merged_groups) toast.success("No duplicate phone numbers found");
      else toast.success(`Merged ${data.duplicates_removed} duplicate${data.duplicates_removed===1?"":"s"} into ${data.merged_groups} existing customer${data.merged_groups===1?"":"s"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed (admin only)"); }
  };

  const exportCsv = () => {
    const token = localStorage.getItem("gaze_token");
    fetch(`${API}/customers/export/csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(b => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = url; a.download = "customers.csv"; a.click();
        URL.revokeObjectURL(url);
      });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Directory</p>
          <h1 className="font-serif-elegant text-5xl mt-1">Customers</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".csv" hidden onChange={importCsv} data-testid="import-input" />
          <Button variant="outline" onClick={()=>fileRef.current?.click()} data-testid="import-csv-btn"><Upload size={14} className="mr-2"/>Import CSV</Button>
          <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn"><Download size={14} className="mr-2"/>Export</Button>
          <Button variant="outline" onClick={mergeDuplicates} data-testid="merge-duplicates-btn"><Merge size={14} className="mr-2"/>Merge duplicates</Button>
          <Dialog open={open} onOpenChange={(v)=>{ setOpen(v); if(!v){ setEditing(null); setForm(emptyForm);} }}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-[#984D38]" data-testid="add-customer-btn"><Plus size={14} className="mr-2"/>Add Customer</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-serif-elegant text-2xl">{editing ? "Edit" : "New"} Customer</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Name (optional)</Label><Input data-testid="customer-name" placeholder="Defaults to “Customer” if left blank" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} /></div>
                <div><Label>Phone</Label><Input data-testid="customer-phone" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} /></div>
                <div><Label>WhatsApp</Label><Input data-testid="customer-whatsapp" value={form.whatsapp} onChange={e=>setForm({...form, whatsapp:e.target.value})} placeholder="+91…" /></div>
                <div className="col-span-2"><Label>Email</Label><Input data-testid="customer-email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} /></div>
                <div className="col-span-2"><Label>Address</Label><Textarea data-testid="customer-address" value={form.address} onChange={e=>setForm({...form, address:e.target.value})} /></div>
                <div>
                  <Label>Tag</Label>
                  <Select value={form.tag} onValueChange={(v)=>setForm({...form, tag:v})}>
                    <SelectTrigger data-testid="customer-tag"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="regular">Regular</SelectItem>
                      <SelectItem value="vip">VIP</SelectItem>
                      <SelectItem value="one-time">One-time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})} /></div>
              </div>
              <DialogFooter><Button onClick={submit} className="bg-primary hover:bg-[#984D38]" data-testid="customer-save">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="p-4 border-border bg-white">
        <div className="flex gap-3 flex-wrap items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="customer-search" placeholder="Search name, phone, email…" value={search} onChange={e=>setSearch(e.target.value)} className="pl-9" />
          </div>
          <Select value={tagFilter} onValueChange={setTagFilter}>
            <SelectTrigger className="w-40" data-testid="tag-filter"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tags</SelectItem>
              <SelectItem value="regular">Regular</SelectItem>
              <SelectItem value="vip">VIP</SelectItem>
              <SelectItem value="one-time">One-time</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground ml-auto">{items.length} customers</span>
        </div>
      </Card>

      <Card className="border-border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground bg-secondary/50">
            <tr>
              <th className="py-3 px-4">Name</th>
              <th className="py-3 px-4">Contact</th>
              <th className="py-3 px-4">Address</th>
              <th className="py-3 px-4">Tag</th>
              <th className="py-3 px-4 text-right">Wallet</th>
              <th className="py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-muted-foreground">No customers yet. Add your first customer.</td></tr>}
            {items.map(c => (
              <tr key={c.id} className="border-t hairline hover:bg-accent/30" data-testid={`customer-row-${c.id}`}>
                <td className="py-3 px-4 font-medium">{c.name}</td>
                <td className="py-3 px-4 text-xs text-muted-foreground">
                  {c.phone && <div>📞 {c.phone}</div>}
                  {c.whatsapp && <div>💬 {c.whatsapp}</div>}
                  {c.email && <div>✉ {c.email}</div>}
                </td>
                <td className="py-3 px-4 max-w-xs truncate text-muted-foreground">{c.address}</td>
                <td className="py-3 px-4"><Badge variant="outline" className={`${tagStyles[c.tag] || ""} capitalize`}>{c.tag}</Badge></td>
                <td className="py-3 px-4 text-right">{Number(c.wallet_balance || 0) > 0 ? <span className="text-primary font-medium">{rupee(c.wallet_balance)}</span> : <span className="text-muted-foreground">—</span>}</td>
                <td className="py-3 px-4 text-right">
                  <Button size="icon" variant="ghost" onClick={()=>{ setEditing(c); setForm(c); setOpen(true); }} data-testid={`edit-${c.id}`}><Pencil size={14}/></Button>
                  <Button size="icon" variant="ghost" onClick={()=>del(c.id)} data-testid={`delete-${c.id}`}><Trash2 size={14} className="text-destructive"/></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
